// =============================================================================
// Receiving — faithful port of POS frmReceivingDashboard / Received_Inventory.
// Lists purchase-order receipts (pack slip, vendor, date); click to view the
// received line items.
// =============================================================================
import { useState } from 'react'
import { X, Plus, Search, Trash2 } from 'lucide-react'
import { listReceiving, getReceivingDetail, createReceiving, lookupProductByCode } from '../services/data'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, FilterBar, Button } from '../components/shared/ui'
import { currency, date, productCode } from '../lib/format'
import { useServerList } from '../lib/list'
import type { Receiving as ReceivingHeader, ReceivingDetailItem, Product } from '../types'
import { toast } from 'react-toastify'

const PAGE_SIZE = 25

export default function Receiving() {
  const [selected, setSelected] = useState<ReceivingHeader | null>(null)
  const [detail, setDetail] = useState<ReceivingDetailItem[]>([])
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort, reload } =
    useServerList<ReceivingHeader>(listReceiving, { pageSize: PAGE_SIZE, defaultSort: { column: 'receive_date', dir: 'desc' } })

  const open = async (r: ReceivingHeader) => {
    setSelected(r); setLoadingDetail(true)
    try { setDetail(await getReceivingDetail(r.id)) } finally { setLoadingDetail(false) }
  }

  return (
    <div>
      <PageHeader title="Receiving" subtitle={`${count.toLocaleString()} purchase-order receipts`}
        actions={<Button onClick={() => setShowNew(true)}><Plus size={16} /> New Receipt</Button>} />
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search pack slip, vendor, shipment…" />
          <FilterBar onChange={setFilters} fields={[{ key: 'date', label: 'Receive Date', column: 'receive_date', type: 'daterange' }]} />
        </div>
        {loading ? <Spinner label="Loading receiving…" /> : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              onRowClick={open}
              columns={[
                { key: 'receive_date', header: 'Date', render: r => date(r.receive_date), sortKey: 'receive_date' },
                { key: 'pack_slip_number', header: 'Pack Slip', render: r => r.pack_slip_number || '—', sortKey: 'pack_slip_number' },
                { key: 'vendor_name', header: 'Vendor', render: r => r.vendor_name || '—', sortKey: 'vendor_name' },
                { key: 'location_name', header: 'Location', render: r => r.location_name || '—', sortKey: 'location_name' },
                { key: 'ship_via', header: 'Ship Via', render: r => r.ship_via || '—', sortKey: 'ship_via' },
                { key: 'shipment_number', header: 'Shipment #', render: r => r.shipment_number || '—', sortKey: 'shipment_number' },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-lg">Receipt — {selected.pack_slip_number || `#${selected.legacy_id ?? ''}`}</h3>
                <p className="text-sm text-gray-500">{selected.vendor_name} · {date(selected.receive_date)}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {loadingDetail ? <Spinner label="Loading items…" /> : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-600 border-b border-gray-200">
                      <th className="text-left font-semibold px-2 py-2">#</th>
                      <th className="text-left font-semibold px-2 py-2">Item</th>
                      <th className="text-right font-semibold px-2 py-2">Qty</th>
                      <th className="text-right font-semibold px-2 py-2">Price</th>
                      <th className="text-right font-semibold px-2 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.length === 0 ? (
                      <tr><td colSpan={5} className="text-center text-gray-400 py-8">No line items</td></tr>
                    ) : detail.map(d => (
                      <tr key={d.id} className="border-b border-gray-100">
                        <td className="px-2 py-1.5 text-gray-500">{d.order_number ?? '—'}</td>
                        <td className="px-2 py-1.5">{d.product_name || d.legacy_product_code}</td>
                        <td className="px-2 py-1.5 text-right">{d.qty}</td>
                        <td className="px-2 py-1.5 text-right">{currency(d.price)}</td>
                        <td className="px-2 py-1.5 text-right font-medium">{currency(d.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </div>
      )}

      {showNew && <ReceivingForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload() }} />}
    </div>
  )
}

// ---- New receipt (Received_Inventory) ---------------------------------------

interface ReceiptLine {
  uid: string
  product: Product | null
  code: string
  name: string
  qty: number
  price: number
}

let _ruid = 0
const newRuid = () => `rl_${++_ruid}`

function ReceivingForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [vendorName, setVendorName] = useState('')
  const [packSlip, setPackSlip] = useState('')
  const [shipVia, setShipVia] = useState('')
  const [shipmentNumber, setShipmentNumber] = useState('')
  const [location, setLocation] = useState('')
  const [freight, setFreight] = useState('')
  const [receiveDate, setReceiveDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [lines, setLines] = useState<ReceiptLine[]>([])
  const [code, setCode] = useState('')
  const [saving, setSaving] = useState(false)

  const addByCode = async () => {
    const c = code.trim()
    if (!c) { setLines(prev => [...prev, { uid: newRuid(), product: null, code: '', name: '', qty: 1, price: 0 }]); return }
    try {
      const p = await lookupProductByCode(c)
      if (p) {
        setLines(prev => [...prev, { uid: newRuid(), product: p, code: productCode(p), name: p.name, qty: 1, price: p.cost_price ?? 0 }])
      } else {
        setLines(prev => [...prev, { uid: newRuid(), product: null, code: c, name: '', qty: 1, price: 0 }])
      }
      setCode('')
    } catch { toast.error('Product lookup failed') }
  }

  const patch = (uid: string, p: Partial<ReceiptLine>) => setLines(prev => prev.map(l => l.uid === uid ? { ...l, ...p } : l))
  const remove = (uid: string) => setLines(prev => prev.filter(l => l.uid !== uid))

  const save = async () => {
    if (!vendorName.trim()) { toast.error('Vendor name is required'); return }
    const valid = lines.filter(l => l.qty > 0)
    if (valid.length === 0) { toast.error('Add at least one received item'); return }
    setSaving(true)
    try {
      await createReceiving(
        {
          vendor_name: vendorName.trim(),
          pack_slip_number: packSlip.trim() || null,
          ship_via: shipVia.trim() || null,
          shipment_number: shipmentNumber.trim() || null,
          location_name: location.trim() || null,
          freight: freight.trim() || null,
          receive_date: new Date(receiveDate).toISOString(),
          is_active: true,
        },
        valid.map(l => ({
          product_id: l.product?.id ?? null,
          legacy_product_code: l.code || null,
          product_name: l.name || l.code,
          qty: l.qty,
          price: l.price,
          total_price: Math.round(l.qty * l.price * 100) / 100,
        })),
      )
      toast.success('Receipt saved — stock updated')
      onSaved()
    } catch (e) { console.error(e); toast.error('Failed to save receipt') } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-semibold text-lg">New Receipt</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <label className="block text-sm"><span className="text-gray-600">Vendor <span className="text-red-500">*</span></span>
              <input value={vendorName} onChange={e => setVendorName(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">Pack Slip #</span>
              <input value={packSlip} onChange={e => setPackSlip(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">Receive Date</span>
              <input type="date" value={receiveDate} onChange={e => setReceiveDate(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">Ship Via</span>
              <input value={shipVia} onChange={e => setShipVia(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">Shipment #</span>
              <input value={shipmentNumber} onChange={e => setShipmentNumber(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">Location</span>
              <input value={location} onChange={e => setLocation(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">Freight</span>
              <input value={freight} onChange={e => setFreight(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
          </div>

          <div>
            <div className="relative max-w-md">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={code} onChange={e => setCode(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addByCode() }}
                placeholder="Scan/enter product code, Enter to add line…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm" />
            </div>
            <table className="w-full text-sm mt-3">
              <thead>
                <tr className="text-gray-600 border-b border-gray-200">
                  <th className="text-left font-semibold px-2 py-2">Code</th>
                  <th className="text-left font-semibold px-2 py-2">Item</th>
                  <th className="text-right font-semibold px-2 py-2 w-24">Qty</th>
                  <th className="text-right font-semibold px-2 py-2 w-28">Unit Cost</th>
                  <th className="text-right font-semibold px-2 py-2 w-28">Total</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-gray-400 py-6">No items yet</td></tr>
                ) : lines.map(l => (
                  <tr key={l.uid} className="border-b border-gray-100">
                    <td className="px-2 py-1.5 text-gray-500">{l.code || '—'}</td>
                    <td className="px-2 py-1.5">
                      <input value={l.name} onChange={e => patch(l.uid, { name: e.target.value })}
                        className="w-full px-2 py-1 rounded border border-gray-200" />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" value={l.qty} min={0} onChange={e => patch(l.uid, { qty: parseFloat(e.target.value) || 0 })}
                        className="w-20 text-right px-2 py-1 rounded border border-gray-200" />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" value={l.price} min={0} onChange={e => patch(l.uid, { price: parseFloat(e.target.value) || 0 })}
                        className="w-24 text-right px-2 py-1 rounded border border-gray-200" />
                    </td>
                    <td className="px-2 py-1.5 text-right font-medium">{currency(l.qty * l.price)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button onClick={() => remove(l.uid)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200">
          <span className="text-sm text-gray-600">Total received: <span className="font-semibold">{currency(lines.reduce((s, l) => s + l.qty * l.price, 0))}</span></span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save & Receive Stock'}</Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
